import {
  ToolContext,
  ToolDefinition,
  ToolId,
} from 'config/toolDefinitionsConfig';
import { TOOL_REGISTRY } from 'config/toolRegistry';
import { useBranchInfo } from '@contexts/BranchContext';
import { useAVCReportModal } from '@features/analysis/analysisModal/AVCReportModal/AVCReportModal';
import {
  AutoCaptureMode,
  useAutoCaptureModal,
} from '@features/analysis/analysisModal/AutoCaptureModal/AutoCaptureModal';
import { useCACReportModal } from '@features/analysis/analysisModal/CACReportModal/CACReportModal';
import { useDicomTagModal } from '@features/analysis/analysisModal/DicomTagModal/DicomTagModal';
import { useEFReportModal } from '@features/analysis/analysisModal/EjectionFractionReportModal/EjectionFractionReportModal';
import { useFAIReportModal } from '@features/analysis/analysisModal/FAIReportModal/FAIModal';
import { useISRReportModal } from '@features/analysis/analysisModal/ISRReportModal/ISRReportModal';
import { useMyoReportModal } from '@features/analysis/analysisModal/MyoReportModal/MyoReportModal';
import { usePlaqueReportModal } from '@features/analysis/analysisModal/PlaqueModal/PlaqueReportModal';
import { useStenosisReportModal } from '@features/analysis/analysisModal/StenosisReportModal/StenosisReportModal';
import { useWallReportModal } from '@features/analysis/analysisModal/WallReportModal/WallModal';
import { useTaviReportModal } from '@features/analysis/analysisModal/TaviReportModal/TaviReportModal';
import { useEditModel } from '@features/analysis/analysisModal/EditModel/EditModel';

import {
  activeExclusiveTools,
  deleteAllAnnotations,
  fitDicomViewportsToScreen,
  getRequestSegmentation,
  getViewportIdsEnabled,
  hideAllAnnotations,
  resetAnother,
  resetViewport,
  syncDicomPanToAxial,
  syncDicomZoomToAxial,
  toggleCrosshairsTool,
  transformViewport,
  updateViewportZoom,
} from '@features/analysis/rendering/Panels/Renderers/CornerstoneManager';
import {
  ContextKey,
  CPRContext,
  CrossSectionContext,
  SurfaceContext,
  useBufferPool,
  VRContext,
} from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import { scheduleRender } from '@features/analysis/rendering/Panels/renderState';
import { usePanelContainer } from '@features/analysis/rendering/Panels/usePanelContainer';
import { useAnalysisPage } from '@features/analysis/useAnalysisPage';
import { useBrowserPage } from '@features/browser/useBrowserPage';
import { fetch_base_info } from 'utils/AnalysisBaseUtilities/AnalysisBaseUtilities';
import { configStore } from 'config/configStore';
import { useConfig } from 'config/useConfig';
import useModalActions from '@hooks/common/useModalAction';
import useAuth from '@hooks/Auth/useAuth';
import { CalciumAnalysisResult } from '@models/Parameter';
import {
  cprMultiCapture,
  cprSingleCapture,
} from 'utils/captureUtils';
import { useEffect, useMemo, useRef } from 'react';
import {
  buildCprServerCaptureSendInfo,
  buildLesionStackServerCaptureSendInfo,
  buildSegmentationServerCaptureSendInfo,
} from 'utils/serverCaptureSendInfo';
import { getConfiguredCprCrossSectionImageMode } from '@features/analysis/rendering/Panels/cprImageModeConfig';
import useFFRPreviewModal from '@features/analysis/analysisModal/FFRReportPreViewModel/useFFRPreviewModal';
import { FFRNames } from 'config/render3DConfig';



const getDefaultToggleValue = (id: ToolId): boolean => {
  switch (id) {
    case 'ToggleDicomZoomSync':
    case 'ToggleDicomPanSync':
      return true;
    default:
      return false;
  }
};

export const useTool = () => {
  const {
    currentVOI,
    panelToolState,
    globalToolState,
    handleUpdatePanelTool,
    handleUpdateToolForPanel,
    handleUpdateGlobalTool,
    isPreview,
    // syncPanelComponentKey,
    panelId,
    selectedAnalysis,
    selectedLayoutConfig,
    // updateSyncCTPanelsToSelected,
    updateSwitchConfigs,
    layoutConfig,
    updateCurrentLayout,
    updateActivatedTool,
    updateWindowLevelWidth,
  } = useAnalysisPage();
  const { panelStates, analysisType, selectedPanelComponentKey } =
    usePanelContainer();

  const { contextPool, bufferPool } = useBufferPool();
  const {
    selectedSeriesId,
    selectedSeriesImageTagWindowLevel,
    selectedSeriesImageTagWindowWidth,
  } = useBrowserPage();

  const poolRef = useRef(contextPool);
  useEffect(() => {
    poolRef.current = contextPool;
  }, [contextPool]);
  const {
    waitingAction,
    closeLoadingModal,
    setWaitingProgressMessage,
    successAction,
  } = useModalActions();
  const { accessToken } = useAuth();
  const defaultMessage = 'Capturing and uploading';
  const pool = bufferPool[selectedSeriesId!];
  const { mainBranchIds } = useBranchInfo();
  const infodata = (pool?.parameter?.data ?? {}) as CalciumAnalysisResult;
  const newtype = infodata ? infodata.SavedInfo?.Coronary?.newType : [];

  const dicomTagModalOpen = useDicomTagModal();
  const FAIReportModalOpen = useFAIReportModal({ updateActivatedTool });
  const WallReportModalOpen = useWallReportModal({ updateActivatedTool });
  const AVCReportModalOpen = useAVCReportModal({ updateActivatedTool });
  const MyoReportModalOpen = useMyoReportModal({ updateActivatedTool });
  const CACReportModalOpen = useCACReportModal({ updateActivatedTool });
  const StenosisModalOpen = useStenosisReportModal({ updateActivatedTool });
  const ISRModalOpen = useISRReportModal({ updateActivatedTool });
  const EFModalOpen = useEFReportModal({ updateActivatedTool });
  const PlaqueModalOpen = usePlaqueReportModal({ updateActivatedTool });
  const TaviModalOpen = useTaviReportModal({ updateActivatedTool });
  const AutoCaptureModalOpen = useAutoCaptureModal();
  const EditModelOpen = useEditModel({ updateActivatedTool });
  const healthCheckupPreviewModalOpen = useFFRPreviewModal();
  const capture3DType: AutoCaptureMode = selectedLayoutConfig.panelConfig.some(
    (panel) => panel.componentKey === 'Surface3D',
  )
    ? 'Surface3DCapture'
    : 'VRCapture';

  // 커스텀 오토 캡쳐 기능 추가 했을때 ui 추가하는 코드
  const { config } = useConfig();
  const customAutoCaptureTools = config.customAutoCaptureTools ?? [];

  const mergedToolRegistry: ToolDefinition[] = useMemo(() => {
    const baseIds = new Set(TOOL_REGISTRY.map((t) => t.id));

    const customDefs: ToolDefinition[] = customAutoCaptureTools
      .map(
        (t) =>
          ({
            id: `AutoCapture_Custom_${t.id}` as ToolId,
            name: t.name,
            group: 'autocapture',
            behavior: 'momentary',
            visibility: { analyses: t.analyses },
          }) as ToolDefinition,
      )
      .filter((t) => !baseIds.has(t.id));

    return [...TOOL_REGISTRY, ...customDefs];
  }, [config]); // ✅ (중요) config로 걸어두면 갱신 누락이 훨씬 줄어듦
  //~~~~ 커스텀 오토 캡쳐 기능 추가 했을때 ui 추가하는 코드

  const createToolContext = (): ToolContext | undefined => {
    if (!selectedPanelComponentKey || !panelId) return;

    return {
      panelType: panelId,
      analysisMode: analysisType,
      selectedPanelComponentKey: selectedPanelComponentKey,
      // syncPanelComponentKey: syncPanelComponentKey,
      activeToolId: panelToolState?.activeToolId,
      activeGroup: panelToolState?.activeGroup,
      layoutConfig: selectedLayoutConfig,
      state: {
        isPreview: isPreview,
        // isSynchronized: !!syncPanelComponentKey,
        isSegmentationManager:
          !!globalToolState?.toggles.ToggleSegmentationManager,
        isShowMeasurement:
          globalToolState?.toggles.ToggleMeasurementVisibility!,
        isToggleMIP: configStore.get().visibleMIP,
      },
      actions: {
        execute, // 👈 여기서 연결
      },
    };
  };

  // mergedToolRegistry 이거 원래 TOOL_REGISTRY 이거 였는데
  // 위에 커스텀 추가되면 바꿔줘야해서 변경
  const getToolDefinition = (id: ToolId): ToolDefinition | undefined =>
    mergedToolRegistry.find((t) => t.id === id);

  const getVisibleToolsForContext = (): ToolDefinition[] => {
    const ctx = createToolContext();
    if (!ctx) return [];

    return mergedToolRegistry.filter((tool) => {
      const { visibility } = tool;
      if (!visibility) return true;

      const { panels, analyses, panelAnalyses } = visibility;
      const panel = ctx.panelType;
      const analysis = ctx.analysisMode;

      const matchesPanel = panels?.includes(panel) ?? false;
      const matchesAnalysis = analyses?.includes(analysis) ?? false;
      const matchesPanelAnalysis = !!panelAnalyses?.[panel]?.includes(analysis);

      const isVisible =
        matchesPanel ||
        matchesAnalysis ||
        matchesPanelAnalysis ||
        (!panels && !analyses && !panelAnalyses);

      if (!isVisible) return false;

      if (tool.enabled && !tool.enabled(ctx)) return false;

      return true;
    });
  };

  const getGroupedToolsForMenu = () => {
    const visibleTools = getVisibleToolsForContext();

    const grouped: Record<string, ToolDefinition[]> = {};
    visibleTools.forEach((tool) => {
      if (!grouped[tool.group]) grouped[tool.group] = [];
      grouped[tool.group].push(tool);
    });

    return grouped;
  };

  const isSegmentationMultiCaptureReady = async () => {
    if (!accessToken || !selectedSeriesId) return false;
    const items = await fetch_base_info(accessToken, selectedSeriesId);
    // const items = Array.isArray(raw) ? raw : [];
    const statusByName = new Map(
      items.map((item) => [item.name.toLowerCase() ?? '', item.status]),
    );
    return (
      statusByName.get('segmentation') === 'Succeeded' &&
      statusByName.get('coronary') === 'Succeeded'
    );
  };

  const handleTransformViewport = (
    ...param: Parameters<typeof transformViewport>
  ) => {
    if (analysisType !== 'chamber') {
      transformViewport(...param);
    } else {
      scheduleRender(selectedPanelComponentKey!, {
        type: 'transformViewport',
        method: param[2],
      });
    }
  };

  const isCPRDataReady = () => {
    if (!selectedSeriesId) return false;
    const pool = bufferPool[selectedSeriesId];
    if (!pool) return false;
    const { renderingQuality } = configStore.get().generalSetting;
    const lowQ = renderingQuality !== 'high';
    if (lowQ) {
      return (
        pool.oriLabelData.loaded &&
        pool.labelData.loaded &&
        pool.centerData.loaded &&
        pool.branchGraphDataset.loaded
      );
    } else {
      return (
        pool.labelData.loaded &&
        pool.centerData.loaded &&
        pool.branchGraphDataset.loaded
      );
    }
  };

  const isCPRContextReady = (key: ContextKey) => {
    const context = contextPool[key] as CPRContext | undefined;
    return (
      !!context?.actorIds?.CPRActor &&
      !!context?.path?.length &&
      !!context?.imageData &&
      !!context?.labelData &&
      !!context?.centerlineData
    );
  };

  const isFFRContextReady = () => {
    if (!selectedSeriesId) return false;
    const pool = bufferPool[selectedSeriesId];
    if (!pool) return false;
    return (
      pool['ct_ffr']?.loaded &&
      (Object.values(FFRNames) as string[]).every((name) => pool[name]?.loaded)
    );
  };

  const isServerCprReportMode = (reportType: string) => {
    if (getConfiguredCprCrossSectionImageMode() !== 'server') return false;
    return (
      reportType === 'plaque' ||
      reportType === 'fai' ||
      reportType === 'isr' ||
      reportType === 'aorticValveCalcium'
    );
  };

  const isCrossSectionReady = (key: ContextKey) => {
    const context = contextPool[key] as CrossSectionContext | undefined;
    return !!context?.actorIds?.CSActor;
  };

  const isSurfaceReady = (key: ContextKey) => {
    const context = contextPool[key] as SurfaceContext | undefined;
    return (
      !!context?.renderWindow && Object.keys(context?.actorIds ?? {}).length > 0
    );
  };

  const isVRReady = (key: ContextKey) => {
    const context = contextPool[key] as VRContext | undefined;
    return (
      !!context?.renderWindow && Object.keys(context?.actorIds ?? {}).length > 0
    );
  };

  const isReportReady = (reportType: string) => {
    if (isServerCprReportMode(reportType)) {
      // In server CPR mode the report CPR images are composed from
      // front_data/view_data.json + cpr_*.zip inside each report modal.
      // Do not wait for legacy VTK CPR render contexts here; those contexts
      // may intentionally not exist in server image mode. AVC still needs the
      // already-rendered cross-section and 3D panels for the rest of the report.
      if (reportType === 'aorticValveCalcium') {
        // Server canvas CrossSection does not create the legacy VTK CSActor,
        // so the old CrossSection readiness check can block AVC report forever.
        // Open the report modal and let each capture step use its server-canvas
        // capture path or safe fallback internally.
        return true;
      }
      return true;
    }

    switch (reportType) {
      case 'plaque':
      case 'isr':
        return isCPRDataReady() && isCPRContextReady('CPR.0.straightened');
      case 'fai': {
        if (!isCPRDataReady()) return false;
        const cprReady = [0, 1, 2].every((idx) =>
          isCPRContextReady(`CPR.${idx}.straightened` as ContextKey),
        );
        if (!cprReady) return false;
        return [0, 1, 2].every((key) =>
          [0, 1, 2].every((idx) =>
            isCrossSectionReady(`CrossSection.${key}.${idx}` as ContextKey),
          ),
        );
      }
      case 'aorticValveCalcium':
        return (
          isCPRContextReady('CPR.0.straightened') &&
          isCrossSectionReady('CrossSection.0.0') &&
          isSurfaceReady('Surface3D')
        );
      case 'stenosis': {
        const hasSurface3D = selectedLayoutConfig.panelConfig.some(
          (panel) => panel.componentKey === 'Surface3D',
        );
        return hasSurface3D ? isSurfaceReady('Surface3D') : isVRReady('VR');
      }
      case 'FFR':{
        return isSurfaceReady('Surface3D') && isFFRContextReady();
      }
      case 'myocardial':
      case 'calciumScore':
      case 'wallthickness':
        return isSurfaceReady('Surface3D');
      case 'ejectionFraction':
        return isVRReady('VR');
      case 'tavi': {
        const currentPool = selectedSeriesId
          ? bufferPool[selectedSeriesId]
          : undefined;
        const parsedReady = !!(currentPool as Record<string, any> | undefined)?.[
          'tavi.parsed'
        ]?.loaded;
        const hasSurface3D = selectedLayoutConfig.panelConfig.some(
          (panel) => panel.componentKey === 'Surface3D',
        );
        const rendererReady = hasSurface3D
          ? isSurfaceReady('Surface3D')
          : isVRReady('VR');
        return parsedReady && rendererReady;
      }
      default:
        return true;
    }
  };

  const waitForReportReady = async (reportType: string, timeoutMs = 3000) => {
    const start = performance.now();
    while (!isReportReady(reportType)) {
      if (performance.now() - start > timeoutMs) return false;
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => resolve()),
      );
    }
    return true;
  };

  function getVisibleSegmentationKeys() {
    const { segmentation } = panelStates['Surface3D'];
    if (!segmentation) return [];
    const seg = getRequestSegmentation(analysisType, segmentation);
    const keys = Object.keys(seg) as (keyof typeof seg)[];
    const visibleKeys = keys.filter((key) => seg[key].visibility);
    return visibleKeys;
  }

  const execute = async (action: string, payload?: any) => {
    switch (action) {
      case 'SET_ACTIVE_TOOL': {
        const { id } = payload;
        handleUpdatePanelTool(id);
        if (panelId === 'DICOMPanel' && id !== 'Rotation') {
          activeExclusiveTools(id);
        }
        break;
      }

      case 'TOGGLE_GLOBAL_TOOL': {
        const { id } = payload;
        const toolId = id as ToolId;
        const current =
          globalToolState?.toggles[toolId] ?? getDefaultToggleValue(toolId);
        const next = !current;

        console.log(id, current);

        switch (id) {
          case 'ToggleAnnotations':
            hideAllAnnotations(!next);
            handleUpdateGlobalTool(id);
            break;
          case 'ToggleAxes':
            toggleCrosshairsTool(next);
            requestAnimationFrame(() => {
              scheduleRender('VR', { type: 'switchAxes', visible: next });
              scheduleRender('Surface3D', {
                type: 'switchAxes',
                visible: next,
              });
            });
            handleUpdateGlobalTool(id);
            configStore.setData({
              annotationState: {
                ...configStore.get().annotationState,
                Axis: next,
              },
            });
            break;
          case 'ToggleMeasurementVisibility':
            hideAllAnnotations(!next);
            handleUpdateGlobalTool(id);
            handleUpdateToolForPanel('DICOMPanel', 'Scroll');
            activeExclusiveTools('Scroll');
            break;
          case 'ToggleDICOMTags':
            dicomTagModalOpen();
            break;
          case 'ToggleSegmentationManager':
            handleUpdateGlobalTool(id);
            break;
          case 'TogglePatientData':
            handleUpdateGlobalTool(id);
            break;
          case 'ToggleDicomZoomSync':
            handleUpdateGlobalTool(id);
            if (next) {
              requestAnimationFrame(() => syncDicomZoomToAxial());
            }
            break;
          case 'ToggleDicomPanSync':
            handleUpdateGlobalTool(id);
            if (next) {
              requestAnimationFrame(() => syncDicomPanToAxial());
            }
            break;
          // case 'ToggleSync':
          //   handleUpdateGlobalTool(id);
          //   if (analysisType === 'segmentation')
          //     scheduleRender('VR', { type: 'runMIP', mode: 'syncCT' });
          //   updateSyncCTPanelsToSelected(selectedPanelComponentKey);
          //   updateSwitchConfigs({
          //     ['switchViewReport']: true,
          //   });
          //   break;
          case 'TogglePlaqueManual':
            const nextLayout =
              selectedLayoutConfig.type === 'plaqueManuel'
                ? '5-2-2'
                : 'plaqueManuel';

            updateCurrentLayout(nextLayout);
            handleUpdateGlobalTool(id);
            break;
          case 'ToggleMIP':
            handleUpdateGlobalTool(id);
            break;
          default:
            break;
        }
        break;
      }

      case 'MOMENTARY_TOOL': {
        const { id } = payload;

        // auto capture 버튼 추가 관련 함수
        if (typeof id === 'string' && id.startsWith('AutoCapture_Custom_')) {
          const key = id.replace('AutoCapture_Custom_', '');
          const tool = customAutoCaptureTools.find((t) => t.id === key);
          if (tool) {
            // 여기에 추가된 버튼에 대한 각 함수 적용
            console.log('check');

            // 이거는 auto capture 버튼 추가하는 방법
            // config에 일단 임시 저장했음
            // 추후에 CustomAutoCaptureTool 여기다가 캡쳐 방식 등 추가해서 사용자 커스텀 내용 정리하면 될듯?
            if (id === 'AutoCapture_Segmentation_3D_MultiView') {
              configStore.setData((prev) => ({
                customAutoCaptureTools: [],
              }));
            } else {
              configStore.setData((prev) => ({
                customAutoCaptureTools: [
                  ...(prev.customAutoCaptureTools ?? []),
                  {
                    id: 'MySegSingle', // suffix
                    name: 'My Seg 3D Single', // UI 표시명
                    analyses: ['segmentation'], // 어느 분석에서 보일지
                    template: 'segmentation3d', // 실행 템플릿
                    options: { single: true }, // 템플릿 옵션
                  },
                ],
              }));
            }
            break;
          }
          break;
        }

        switch (id) {
          case 'ResetView':
            deleteAllAnnotations();
            if (analysisType === 'tavi') {
              configStore.setData({
                taviPlaneOverrides: {
                  annulus: null,
                  sinus: null,
                  sinotubularjunction: null,
                },
              });
            }
            resetAnother(analysisType);
            const { useDefaultWindow, defaultWindowLevel, defaultWindowWidth } =
              configStore.get().analysisParams[analysisType] || {};
            let wl = useDefaultWindow
              ? defaultWindowLevel
              : selectedSeriesImageTagWindowLevel;
            let ww = useDefaultWindow
              ? defaultWindowWidth
              : selectedSeriesImageTagWindowWidth;
            updateWindowLevelWidth([wl, ww]);
            if (getViewportIdsEnabled(['Axial','Coronal','Sagittal'])) {
              resetViewport(
                false,
                globalToolState?.toggles.ToggleDicomZoomSync ?? true,
              );
              if(analysisType === 'ejectionFraction'){
                scheduleRender('Axial', { type: 'reset' });
                scheduleRender('Coronal', { type: 'reset' });
                scheduleRender('Sagittal', { type: 'reset' });
              }
            }else{
              resetViewport(false);
            }
            if (!globalToolState?.toggles.ToggleAnnotations)
              handleToolActivate('ToggleAnnotations');
            if (!globalToolState?.toggles.ToggleAxes)
              handleToolActivate('ToggleAxes');
            if (!globalToolState?.toggles.TogglePatientData)
              handleToolActivate('TogglePatientData');
            if (!globalToolState?.toggles.ToggleMeasurementVisibility)
              handleToolActivate('ToggleMeasurementVisibility');
            if (
              analysisType === 'tavi' &&
              selectedSeriesId &&
              pool?.['tavi.parsed']?.data
            ) {
              // requestAnimationFrame(() => {
              //   const vrContext = contextPool['VR'] as VRContext | null;
              //   const surfaceContext = contextPool['Surface3D'] as SurfaceContext | null;

              //   if (vrContext) {
              //     taviNodeAllRenderer(
              //       { current: vrContext },
              //       pool,
              //       selectedSeriesId,
              //     );
              //   }
              //   if (surfaceContext) {
              //     taviNodeAllRenderer(
              //       { current: surfaceContext },
              //       pool,
              //       selectedSeriesId,
              //     );
              //   }

              //   scheduleRender('VR', { type: 'updateRenderWindow' });
              //   scheduleRender('Surface3D', { type: 'updateRenderWindow' });
              // });
            }
            break;
          case 'Report':
            const reportType = selectedAnalysis?.analysisType ?? '';
            if (!isReportReady(reportType)) {
              waitingAction({ message: 'Preparing report' });
              const ready = await waitForReportReady(reportType);
              closeLoadingModal();
              if (!ready) {
                await successAction(
                  'Rendering is not ready yet. Please wait and try again.',
                );
                break;
              }
            }
            const modalMap: Record<string, () => void> = {
              plaque: PlaqueModalOpen,
              fai: FAIReportModalOpen,
              wallthickness: WallReportModalOpen,
              aorticValveCalcium: AVCReportModalOpen,
              myocardial: MyoReportModalOpen,
              calciumScore: CACReportModalOpen,
              stenosis: StenosisModalOpen,
              isr: ISRModalOpen,
              ejectionFraction: EFModalOpen,
              tavi: TaviModalOpen,
              FFR: healthCheckupPreviewModalOpen,
            };
            modalMap[selectedAnalysis?.analysisType ?? '']?.();
            break;
          case 'AutoCapture_Segmentation_3D_SingleCapture':
          case 'AutoCapture_Segmentation_3D_MultiView': {
            if (id === 'AutoCapture_Segmentation_3D_MultiView') {
              const isReady = await isSegmentationMultiCaptureReady();
              if (!isReady) {
                await successAction(
                  'Segmentation is not ready yet. Please wait until it completes.',
                );
                break;
              }
            }
            configStore.setData({ isCapture: true });
            waitingAction({ message: defaultMessage });
            const hasSurface3D = selectedLayoutConfig.panelConfig.some(
              (panel) => panel.componentKey === 'Surface3D',
            );
            let type = '' as AutoCaptureMode;
            if (hasSurface3D) {
              type = 'Surface3DCapture';
            } else {
              type = 'VRCapture';
            }
            const visibleKeys = getVisibleSegmentationKeys();
            const single = id === 'AutoCapture_Segmentation_3D_SingleCapture';
            const d3Context = hasSurface3D
              ? (poolRef.current.Surface3D as SurfaceContext | undefined)
              : (poolRef.current.VR as VRContext | undefined);
            const segmentation = panelStates['Surface3D']?.segmentation;
            const serverSendInfo = buildSegmentationServerCaptureSendInfo({
              type,
              single,
              context: d3Context,
              analysisType,
              currentVOI,
              config: configStore.get(),
              visibleKeys,
              segmentation,
            });
            closeLoadingModal();
            AutoCaptureModalOpen(type, undefined, { serverSendInfo });
            configStore.setData({ isCapture: false });
            break;
          }
          case 'AutoCapture_Chamber_SingleView': {
            configStore.setData({ isCapture: true });
            waitingAction({ message: defaultMessage });
            let type = '' as AutoCaptureMode;
            if (selectedPanelComponentKey === 'Axial') {
              type = 'axialCapture';
            } else if (selectedPanelComponentKey === 'TwoChamber') {
              type = '2chamberCapture';
            } else if (selectedPanelComponentKey === 'ShortAxis') {
              type = 'shortaxisCapture';
            } else if (selectedPanelComponentKey === 'FourChamber') {
              type = '4chamberCapture';
            }
            if (type) {
              // const datas = await chamberCapture(
              //   type,
              //   poolRef,
              //   panelStates,
              //   currentVOI,
              //   selectedSeriesId!,
              //   setWaitingProgressMessage,
              // );
              closeLoadingModal();
              // AutoCaptureModalOpen(type, datas);
              AutoCaptureModalOpen(type, []);
            }
            configStore.setData({ isCapture: false });
            break;
          }
          case 'AutoCapture_Chamber_FullView': {
            configStore.setData({ isCapture: true });
            waitingAction({ message: defaultMessage });
            // const datas = await chamberCapture(
            //   'fullChamberCapture',
            //   poolRef,
            //   panelStates,
            //   currentVOI,
            //   selectedSeriesId!,
            //   setWaitingProgressMessage,
            // );
            closeLoadingModal();
            // AutoCaptureModalOpen('fullChamberCapture', datas);
            AutoCaptureModalOpen('fullChamberCapture', []);
            configStore.setData({ isCapture: false });
            break;
          }
          case 'AutoCapture_Plaque_LesionStackCapture': {
            configStore.setData({ isCapture: true });
            waitingAction({ message: defaultMessage });
            const bi = panelStates['CPR'].coronary?.current[0];
            const branchPoints =
              bi !== undefined
                ? panelStates['CPR'].coronary?.state?.[bi] ?? []
                : [];
            const proximalIndex = Number(branchPoints[0]?.index);
            const distalIndex = Number(branchPoints[2]?.index);
            const cprContext = poolRef.current[
              'CPR.0.stretched' as ContextKey
            ] as CPRContext | undefined;
            const serverSendInfo = buildLesionStackServerCaptureSendInfo({
              currentVOI,
              config: configStore.get(),
              cprContext,
              currentBranchId: bi,
              proximalIndex,
              distalIndex,
              newtype,
            });
            closeLoadingModal();
            AutoCaptureModalOpen('LesionStackCapture', undefined, {
              serverSendInfo,
            });
            configStore.setData({ isCapture: false });
            break;
          }
          case 'AutoCapture_Plaque_SingleCPRView': {
            configStore.setData({ isCapture: true });
            waitingAction({ message: defaultMessage });
            const bi = panelStates['CPR'].coronary?.current[0];
            const visibleKeys = getVisibleSegmentationKeys();

            if (getConfiguredCprCrossSectionImageMode() === 'server') {
              const d3Context =
                capture3DType === 'Surface3DCapture'
                  ? (poolRef.current.Surface3D as SurfaceContext | undefined)
                  : (poolRef.current.VR as VRContext | undefined);
              const cprContexts = {
                stretched: poolRef.current[
                  'CPR.0.stretched' as ContextKey
                ] as CPRContext | undefined,
                straightened: poolRef.current[
                  'CPR.0.straightened' as ContextKey
                ] as CPRContext | undefined,
              };
              const segmentation = panelStates['Surface3D']?.segmentation;
              const serverSendInfo = buildCprServerCaptureSendInfo({
                type: 'CPRSingleCapture',
                capture3DType,
                context: d3Context,
                cprContexts,
                analysisType,
                currentVOI,
                config: configStore.get(),
                visibleKeys,
                segmentation,
                newtype,
                currentBranchId: bi,
              });
              closeLoadingModal();
              AutoCaptureModalOpen('CPRSingleCapture', undefined, {
                serverSendInfo,
              });
            } else {
              const name = newtype
                ? bi !== undefined
                  ? newtype[bi][0]
                  : ''
                : '';
              const data = await cprSingleCapture(
                poolRef,
                name,
                selectedSeriesId!,
                analysisType!,
                capture3DType,
                pool!,
                currentVOI,
                visibleKeys,
                bi,
              );
              closeLoadingModal();
              AutoCaptureModalOpen('CPRSingleCapture', data);
            }

            configStore.setData({ isCapture: false });
            break;
          }
          case 'AutoCapture_Plaque_MainCPRView': {
            configStore.setData({ isCapture: true });
            waitingAction({ message: defaultMessage });
            const visibleKeys = getVisibleSegmentationKeys();
            const bi = panelStates['CPR'].coronary?.current[0];

            if (getConfiguredCprCrossSectionImageMode() === 'server') {
              const d3Context =
                capture3DType === 'Surface3DCapture'
                  ? (poolRef.current.Surface3D as SurfaceContext | undefined)
                  : (poolRef.current.VR as VRContext | undefined);
              const cprContexts = {
                stretched: poolRef.current[
                  'CPR.0.stretched' as ContextKey
                ] as CPRContext | undefined,
                straightened: poolRef.current[
                  'CPR.0.straightened' as ContextKey
                ] as CPRContext | undefined,
              };
              const segmentation = panelStates['Surface3D']?.segmentation;
              const serverSendInfo = buildCprServerCaptureSendInfo({
                type: 'CPRMainCapture',
                capture3DType,
                context: d3Context,
                cprContexts,
                analysisType,
                currentVOI,
                config: configStore.get(),
                visibleKeys,
                segmentation,
                newtype,
                currentBranchId: bi,
              });
              closeLoadingModal();
              AutoCaptureModalOpen('CPRMainCapture', undefined, {
                serverSendInfo,
              });
            } else {
              const data = await cprMultiCapture(
                poolRef,
                pool,
                'CPRMainCapture',
                selectedSeriesId!,
                analysisType!,
                capture3DType,
                newtype,
                currentVOI,
                visibleKeys,
                bi ?? 0,
              );
              closeLoadingModal();
              AutoCaptureModalOpen('CPRMainCapture', data);
            }

            configStore.setData({ isCapture: false });
            break;
          }
          case 'AutoCapture_Plaque_FullCPRView': {
            configStore.setData({ isCapture: true });
            waitingAction({ message: defaultMessage });
            const visibleKeys = getVisibleSegmentationKeys();
            const bi = panelStates['CPR'].coronary?.current[0];

            if (getConfiguredCprCrossSectionImageMode() === 'server') {
              const d3Context =
                capture3DType === 'Surface3DCapture'
                  ? (poolRef.current.Surface3D as SurfaceContext | undefined)
                  : (poolRef.current.VR as VRContext | undefined);
              const cprContexts = {
                stretched: poolRef.current[
                  'CPR.0.stretched' as ContextKey
                ] as CPRContext | undefined,
                straightened: poolRef.current[
                  'CPR.0.straightened' as ContextKey
                ] as CPRContext | undefined,
              };
              const segmentation = panelStates['Surface3D']?.segmentation;
              const serverSendInfo = buildCprServerCaptureSendInfo({
                type: 'CPRFullCapture',
                capture3DType,
                context: d3Context,
                cprContexts,
                analysisType,
                currentVOI,
                config: configStore.get(),
                visibleKeys,
                segmentation,
                newtype,
                currentBranchId: bi,
              });
              closeLoadingModal();
              AutoCaptureModalOpen('CPRFullCapture', undefined, {
                serverSendInfo,
              });
            } else {
              const data = await cprMultiCapture(
                poolRef,
                pool,
                'CPRFullCapture',
                selectedSeriesId!,
                analysisType!,
                capture3DType,
                newtype,
                currentVOI,
                visibleKeys,
                bi ?? 0,
              );
              closeLoadingModal();
              AutoCaptureModalOpen('CPRFullCapture', data);
            }

            configStore.setData({ isCapture: false });
            break;
          }
          case 'RotateCW90':
            handleTransformViewport(
              selectedPanelComponentKey!,
              'MONITOR1',
              'rotateCW90',
            );
            break;
          case 'RotateCCW90':
            handleTransformViewport(
              selectedPanelComponentKey!,
              'MONITOR1',
              'rotateCCW90',
            );
            break;
          case 'FlipVertical':
            handleTransformViewport(
              selectedPanelComponentKey!,
              'MONITOR1',
              'flipVertical',
            );
            break;
          case 'FlipHorizontal':
            handleTransformViewport(
              selectedPanelComponentKey!,
              'MONITOR1',
              'flipHorizontal',
            );
            break;
          case 'FitToScreen':
            if (
              ['Axial', 'Coronal', 'Sagittal'].includes(
                selectedPanelComponentKey!,
              )
            ) {
              fitDicomViewportsToScreen({
                syncToAxial:
                  globalToolState?.toggles.ToggleDicomZoomSync ?? true,
              });
            } else {
              updateViewportZoom(selectedPanelComponentKey!, 0);
            }
            break;
          case 'OneOnOneResolution':
            if (
              (globalToolState?.toggles.ToggleDicomZoomSync ?? true) &&
              ['Axial', 'Coronal', 'Sagittal'].includes(
                selectedPanelComponentKey!,
              )
            ) {
              updateViewportZoom('Axial', 1);
              requestAnimationFrame(() => syncDicomZoomToAxial());
            } else {
              updateViewportZoom(selectedPanelComponentKey!, 1);
            }
            break;
          case 'Edit':
            //열려있으면 닫아야대
            if (
              !config.EditMode &&
              globalToolState?.toggles.ToggleSegmentationManager
            ) {
              handleToolActivate('ToggleSegmentationManager')
            }


            EditModelOpen();
            break;
          default:
            break;
        }
        break;
      }

      default:
        console.warn('⚠️ Unknown tool action:', action);
        break;
    }
  };

  const handleToolActivate = (id: ToolId) => {
    const toolDef = getToolDefinition(id);
    if (!toolDef) return;

    switch (toolDef.behavior) {
      case 'exclusive':
        execute('SET_ACTIVE_TOOL', { id });
        break;
      case 'toggle':
        execute('TOGGLE_GLOBAL_TOOL', { id });
        break;
      case 'momentary': {
        execute('MOMENTARY_TOOL', { id });
        break;
      }
    }
  };

  return {
    createToolContext,
    handleToolActivate,
    getToolDefinition,
    getVisibleToolsForContext,
    getGroupedToolsForMenu,
  };
};
